"use client";

import { Property, PropertyCharacteristics, PropertyStatus } from "@/lib/types/marketsuite/datatypes/property";
import Card, { CardContent, CardFigure, type CardVariant } from "@/components/frontend/ui/card";
import NextLink from "next/link";
import { BedDouble, Scan, Zap } from "lucide-react";
import { FavoriteToggle } from "@/components/frontend/marketsuite/favorite/favorite-toggle";
import { addressToString, priceToString } from "@/lib/marketsuite/helpers";
import { capitalizeWords } from "@/lib/utils/string";
import { buildUrl } from "@/lib/utils/url";
import { trackPropertySelectItem } from "@/lib/data-measurements/property-ecommerce";
import { Image } from "@/components/frontend/ui/image";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import housePlaceholder from "@/assets/house-placeholder.png";

interface PropertyCardProps {
	property: Partial<Property>;
	variant: CardVariant;
	urlBase: string;
	itemListId?: string;
	itemListName?: string;
	itemIndex?: number;
	imagePriorityLoad?: boolean;
}

function getAvailabilityLabel(
	status: PropertyStatus | undefined,
	availableAt: PropertyCharacteristics["available_at"],
	availableFromLabel: string,
	directlyAvailableLabel: string,
) {
	if (!availableAt) {
		if (status === "available") return directlyAvailableLabel;
		return null;
	}

	const date = new Date(availableAt);

	if (date > new Date()) {
		return `${availableFromLabel}: ${date.toLocaleDateString("nl-NL")}`;
	} else if (date <= new Date()) {
		return directlyAvailableLabel;
	} else {
		return null;
	}
}

const statusBadgeColor: Record<PropertyStatus, string> = {
	preparation: "bg-slate-500",
	"pre-order": "bg-sky-500",
	available: "bg-emerald-500",
	pending_offer: "bg-amber-500",
	option: "bg-orange-500",
	with_contingencies: "bg-rose-600",
	unavailable: "bg-red-500",
	occupied: "bg-red-700",
	unknown: "bg-gray-400",
	draft: "bg-gray-500",
};

const PropertyCard: React.FC<PropertyCardProps> = ({ property, variant, urlBase, itemListId, itemListName, itemIndex, imagePriorityLoad }) => {
	const t = useTranslations("ms");

	return (
		<article className="relative h-full">
			<NextLink
				href={buildUrl(urlBase, property.slug ?? "")}
				className="h-full"
				onClick={() => {
					if (itemListId && itemListName) {
						trackPropertySelectItem(property, itemListId, itemListName, itemIndex);
					}
				}}
			>
				<Card hover className="relative h-full" variant={variant}>
					<CardFigure>
						<Image
							blurhash={property?.media?.primary_photo?.blurhash}
							priorityLoad={imagePriorityLoad}
							src={property?.media?.primary_photo?.sm ?? housePlaceholder}
							alt={`Photo of ${property.address ? addressToString(property.address) : (property.title ?? "property")}`}
							width={800}
							height={600}
						/>

						{property.id && property.listing_type && (
							// TEMP HARDCODED PADDING
							<FavoriteToggle propertyId={property.id} listingType={property.listing_type} className="absolute top-4 right-4 p-2!" />
						)}

						{property.status && (
							// TEMP HARDCODED STATUS BADGE
							<div className={cn("py-1 px-3 absolute left-4 top-4 rounded text-sm text-white", statusBadgeColor[property.status])}>
								{t.has(`characteristics.values.${property.status}`)
									? t(`characteristics.values.${property.status}`)
									: capitalizeWords(property.status)}
							</div>
						)}
					</CardFigure>

					<CardContent className="h-full">
						<p className="h4 text-bold text-lg grow">{property.title}</p>
						<div className="flex flex-col gap-1 text-sm">
							{property.address ? <span>{addressToString(property.address)}</span> : null}
							{property.handover?.price ? <div className="font-bold text-lg">{priceToString(property, t)}</div> : null}

							<div className="flex flex-wrap gap-4 text-xs">
								{property.characteristics?.total_area && (
									<span className="flex items-center gap-1">
										<Scan className="size-4" />
										{property.characteristics.total_area} m²
									</span>
								)}

								{property.characteristics?.energy_label && (
									<span className="flex items-center gap-1">
										<Zap className="size-4" />
										{property.characteristics.energy_label}
									</span>
								)}

								{property.characteristics?.number_of_bedrooms && (
									<span className="flex items-center gap-1">
										<BedDouble className="size-4" />
										{property.characteristics.number_of_bedrooms}
									</span>
								)}
							</div>
						</div>

						<div className="text-sm">
							{getAvailabilityLabel(
								property.status,
								property.characteristics?.available_at,
								t("property-card.available-from"),
								t("property-card.directly-available"),
							)}
						</div>
					</CardContent>
				</Card>
			</NextLink>
		</article>
	);
};

export default PropertyCard;
